import * as signalR from "@microsoft/signalr";
import { useEffect, useRef } from "react";
import { logout } from "../pages/Settings/logout";

// Required. The full hub URL, used as given.
const NOTIFICATION_HUB_URL = (
  process.env.REACT_APP_NOTIFICATION_HUB_URL || ""
).trim();

const getStoredToken = () => {
  const loginInfo = JSON.parse(localStorage.getItem("loginInfo") || "{}");
  return loginInfo?.token || "";
};

const isSignalRAuthError = (error) => {
  if (!error) return false;

  const errorMessage = `${error?.message || error || ""}`.toLowerCase();

  return (
    error?.statusCode === 401 ||
    errorMessage.includes("401") ||
    errorMessage.includes("unauthorized") ||
    errorMessage.includes("not authorized") ||
    errorMessage.includes("token expired") ||
    errorMessage.includes("access token")
  );
};

function useNotificationSignalR({ enabled, onNotification, onReconnected }) {
  const onNotificationRef = useRef(onNotification);
  const onReconnectedRef = useRef(onReconnected);

  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    onReconnectedRef.current = onReconnected;
  }, [onReconnected]);

  useEffect(() => {
    if (!enabled) return undefined;

    const token = getStoredToken();
    const hubUrl = NOTIFICATION_HUB_URL;

    if (!hubUrl) {
      console.error(
        "REACT_APP_NOTIFICATION_HUB_URL is not set, so live notifications are off.",
      );
      return undefined;
    }
    if (!token) return undefined;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => getStoredToken(),
        withCredentials: false,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .configureLogging(
        process.env.NODE_ENV === "development"
          ? signalR.LogLevel.Information
          : signalR.LogLevel.Warning,
      )
      .build();

    connection.on("ReceiveNotification", (message) => {
      onNotificationRef.current?.(message);
    });

    connection.onreconnecting((error) => {
      if (isSignalRAuthError(error)) {
        logout();
      }
    });

    // A socket only carries what happens while it is connected. Anything sent while
    // the user was on another tab or mid-reconnect never reaches it, so the feed has
    // to be re-read on every reconnection — not only on the first connect.
    connection.onreconnected(() => {
      onReconnectedRef.current?.();
    });

    connection.onclose((error) => {
      if (isSignalRAuthError(error)) {
        logout();
      }
    });

    connection
      .start()
      .then(() => {
        // WebSockets is what we want and ServerSentEvents is fine (this hub only
        // pushes server->client). Long polling alone is a host misconfiguration —
        // say so in every environment rather than working around it here.
        //
        // Not constructor.name: CRA's production Terser mangles class names, so
        // that check reads a short mangled identifier and never matches in the
        // build where the warning matters. SignalR sets this feature flag only
        // for LongPollingTransport, and property names survive minification.
        if (connection.connection?.features?.inherentKeepAlive === true) {
          console.warn(
            "SignalR notification hub negotiated long polling. The server is not offering WebSockets or ServerSentEvents — report this as infrastructure.",
          );
        } else if (process.env.NODE_ENV === "development") {
          console.info(
            "SignalR notification transport:",
            connection.connection?.transport?.constructor?.name || "unknown",
          );
        }
      })
      .catch((error) => {
        if (isSignalRAuthError(error)) {
          logout();
          return;
        }
        console.error("SignalR notification connection failed:", error);
      });

    return () => {
      connection.off("ReceiveNotification");
      connection.stop().catch((error) => {
        console.error("SignalR notification disconnect failed:", error);
      });
    };
  }, [enabled]);
}

export default useNotificationSignalR;
